import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  decrementPendingQuantitySave,
  incrementPendingQuantitySave,
} from '~/redux/reducers/cart';

/**
 * Reports "a quantity save is in flight" to the global `pendingQuantitySaveCount`
 * Redux counter that CartSummary gates the Place Order button on. Exposes a
 * version-guarded begin/end pair so a caller with its own debounce window
 * (order-builder CartCard) can increment as soon as an edit starts and only
 * decrement once the *latest* edit's save has settled — an older save settling
 * after a newer edit was queued must not prematurely clear the pending flag.
 *
 * Also decrements automatically on unmount if a save was left pending (e.g. the
 * row is removed mid-save), so a dropped decrement can never permanently
 * disable Place Order — combined with the reducer's floor at 0
 * (decrementPendingQuantitySave), the count can't go negative or get stuck
 * above 0 from a single component's lifecycle.
 */
export function usePendingQuantitySave() {
  const dispatch = useDispatch();
  const pendingRef = useRef(false);
  const versionRef = useRef(0);

  useEffect(() => {
    return () => {
      if (pendingRef.current) {
        pendingRef.current = false;
        dispatch(decrementPendingQuantitySave());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount-only cleanup: undoes the increment if the component is removed mid-save
  }, []);

  /** Call when a new edit/save starts. Returns a version token to pass to `end`. */
  const begin = (): number => {
    versionRef.current += 1;
    if (!pendingRef.current) {
      pendingRef.current = true;
      dispatch(incrementPendingQuantitySave());
    }
    return versionRef.current;
  };

  /**
   * Call when the save started by `begin()` settles. Only clears the pending
   * flag if no newer edit has started since (otherwise a still-in-flight
   * debounced save would be masked as "not saving" the moment the older one
   * resolves).
   */
  const end = (version: number) => {
    if (version === versionRef.current && pendingRef.current) {
      pendingRef.current = false;
      dispatch(decrementPendingQuantitySave());
    }
  };

  return { begin, end };
}

/**
 * Convenience wrapper for callers that already track pending-ness via a
 * mutation's `isPending` boolean rather than their own debounce window —
 * mirrors `isPending` into the shared counter whenever it transitions.
 */
export function useTrackPendingQuantitySave(isPending: boolean) {
  const { begin, end } = usePendingQuantitySave();
  const wasPendingRef = useRef(false);
  const versionRef = useRef<number | null>(null);

  useEffect(() => {
    if (isPending && !wasPendingRef.current) {
      versionRef.current = begin();
    } else if (
      !isPending &&
      wasPendingRef.current &&
      versionRef.current != null
    ) {
      end(versionRef.current);
      versionRef.current = null;
    }
    wasPendingRef.current = isPending;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- begin/end close over stable per-instance refs; only isPending transitions should re-run this
  }, [isPending]);
}
